import { HttpCoreService } from "../services/HttpCoreService";
import {
  RegulatoryApproval,
  ApprovalFilters,
  ApprovalSummary,
  ExpiryAlert,
  DocumentVersion,
  TimelineEvent,
  ActivationStatus,
  RegulatoryAuthority,
  ApprovalCategory,
  ApprovalType,
  ApprovalStatus,
  ExpiryAlertSeverity,
} from "../types/regulatory/regulatory.type";
/** Map raw approval from expiry-alerts API to ExpiryAlert (compute daysToExpiry and severity). */
function mapApprovalToExpiryAlert(a: Record<string, unknown>): ExpiryAlert {
  const validUntil = (a.validUntil ?? a.valid_until) as string | undefined;
  const expiryDate = validUntil ? new Date(validUntil).toISOString() : "";
  const now = Date.now();
  const daysToExpiry = validUntil
    ? Math.floor((new Date(validUntil).getTime() - now) / (1000 * 60 * 60 * 24))
    : 0;
  const severity =
    daysToExpiry < 0 || daysToExpiry <= 7
      ? ExpiryAlertSeverity.CRITICAL
      : ExpiryAlertSeverity.WARNING;
  const study = a.study as { name?: string } | undefined;
  const site = a.site as { name?: string } | undefined;
  return {
    id: String(a.id ?? ""),
    approvalId: String(a.id ?? ""),
    approvalNumber: String(a.approvalNumber ?? a.approval_number ?? ""),
    studyName: String(a.studyName ?? study?.name ?? ""),
    siteName: (a.siteName ?? site?.name) as string | undefined,
    siteId: (a.siteId ?? a.site_id) as string | undefined,
    approvalType: (a.approvalType ?? a.approval_type) as ApprovalType,
    category: a.category as ApprovalCategory,
    issuingAuthority: String(a.issuingAuthority ?? a.issuing_authority ?? ""),
    expiryDate,
    daysToExpiry,
    severity,
    status: (a.status as ApprovalStatus) ?? "PENDING",
  };
}
export interface IRegulatoryRepository {
  // Approval operations
  getApprovals(
    studyId: string,
    filters?: ApprovalFilters,
  ): Promise<RegulatoryApproval[]>;
  getApprovalById(
    studyId: string,
    approvalId: string,
  ): Promise<RegulatoryApproval>;
  createApproval(
    studyId: string,
    approval: Omit<RegulatoryApproval, "id" | "createdBy">,
  ): Promise<RegulatoryApproval>;
  updateApproval(
    studyId: string,
    approvalId: string,
    updates: Partial<RegulatoryApproval>,
  ): Promise<RegulatoryApproval>;
  archiveApproval(studyId: string, approvalId: string): Promise<void>;
  // Document operations
  uploadDocument(
    studyId: string,
    approvalId: string,
    file: File,
    versionNotes?: string,
  ): Promise<DocumentVersion>;
  getDocumentVersions(
    studyId: string,
    approvalId: string,
  ): Promise<DocumentVersion[]>;
  downloadDocument(documentUrl: string): Promise<Blob>;
  downloadDocumentStream(
    studyId: string,
    approvalId: string,
    documentId: string,
  ): Promise<Blob>;
  // Timeline operations
  getTimeline(studyId: string, approvalId: string): Promise<TimelineEvent[]>;
  // Summary and alerts
  getApprovalSummary(studyId: string): Promise<ApprovalSummary>;
  getExpiryAlerts(studyId: string, withinDays?: number): Promise<ExpiryAlert[]>;
  getExpiryAlertsBySiteId(
    studyId: string,
    siteId: string,
    withinDays?: number,
  ): Promise<ExpiryAlert[]>;
  // Activation workflow
  checkActivationStatus(
    studyId: string,
    siteId: string,
  ): Promise<ActivationStatus>;
  // Site-specific approvals
  getApprovalsBySiteId(
    studyId: string,
    siteId: string,
    filters?: ApprovalFilters,
  ): Promise<RegulatoryApproval[]>;
  // Regulatory authorities
  getAuthorities(
    category?: ApprovalCategory,
    country?: string,
    active?: boolean,
  ): Promise<RegulatoryAuthority[]>;
}
export class RegulatoryRepository implements IRegulatoryRepository {
  constructor(private httpService: HttpCoreService) {}
  async getApprovals(
    studyId: string,
    filters?: ApprovalFilters,
  ): Promise<RegulatoryApproval[]> {
    let url = `/studies/${studyId}/regulatory/approvals`;
    const params = new URLSearchParams();
    // Always add pagination parameters with defaults
    params.append("page", filters?.page ? String(filters.page) : "1");
    params.append("limit", filters?.limit ? String(filters.limit) : "20");
    // Add optional filter parameters (map camelCase -> snake_case expected by API)
    if (filters) {
      if (filters.study) {
        params.append("study_id", filters.study);
      }
      if (filters.site) {
        params.append("site_id", filters.site);
      }
      if (filters.status) {
        params.append("status", String(filters.status).toUpperCase());
      }
      if (filters.category) {
        params.append("category", String(filters.category).toUpperCase());
      }
      if (filters.approvalType) {
        params.append(
          "approval_type",
          String(filters.approvalType).toUpperCase(),
        );
      }
      if (filters.showArchived !== undefined) {
        params.append("includeInactive", String(filters.showArchived));
      }
      if (filters.expiringWithinDays) {
        params.append(
          "expiring_within_days",
          String(filters.expiringWithinDays),
        );
      }
      if (filters.searchTerm) {
        params.append("search_term", filters.searchTerm);
      }
    }
    url += `?${params.toString()}`;
    const response = await this.httpService.get(url);
    // Handle API response structure: { statusCode, message, data: { items: [...], total, page, limit } }
    if (
      response.data &&
      typeof response.data === "object" &&
      "data" in response.data &&
      response.data.data &&
      typeof response.data.data === "object" &&
      "items" in response.data.data &&
      Array.isArray(response.data.data.items)
    ) {
      return response.data.data.items as RegulatoryApproval[];
    }
    // Fallback if response.data is already the data object with items
    if (
      response.data &&
      typeof response.data === "object" &&
      "items" in response.data &&
      Array.isArray(response.data.items)
    ) {
      return response.data.items as RegulatoryApproval[];
    }
    // Fallback if response is already an array
    if (Array.isArray(response.data)) {
      return response.data as RegulatoryApproval[];
    }
    return [];
  }
  async getApprovalById(
    studyId: string,
    approvalId: string,
  ): Promise<RegulatoryApproval> {
    const response = await this.httpService.get(
      `/studies/${studyId}/regulatory/approvals/${approvalId}`,
    );
    return response.data as RegulatoryApproval;
  }
  async createApproval(
    studyId: string,
    approval: Omit<RegulatoryApproval, "id" | "createdBy">,
  ): Promise<RegulatoryApproval> {
    const response = await this.httpService.post(
      `/studies/${studyId}/regulatory/approvals`,
      approval,
    );
    return response.data as RegulatoryApproval;
  }
  async updateApproval(
    studyId: string,
    approvalId: string,
    updates: Partial<RegulatoryApproval>,
  ): Promise<RegulatoryApproval> {
    const response = await this.httpService.patch(
      `/studies/${studyId}/regulatory/approvals/${approvalId}`,
      updates,
    );
    return response.data as RegulatoryApproval;
  }
  async archiveApproval(studyId: string, approvalId: string): Promise<void> {
    await this.httpService.post(
      `/studies/${studyId}/regulatory/approvals/${approvalId}/archive`,
      {},
    );
  }
  async uploadDocument(
    studyId: string,
    approvalId: string,
    file: File,
    versionNotes?: string,
  ): Promise<DocumentVersion> {
    const formData = new FormData();
    formData.append("file", file);
    if (versionNotes) {
      formData.append("version_notes", versionNotes);
    }
    const response = await this.httpService.postForm(
      `/studies/${studyId}/regulatory/approvals/${approvalId}/documents`,
      formData,
    );
    return response.data as DocumentVersion;
  }
  async getDocumentVersions(
    studyId: string,
    approvalId: string,
  ): Promise<DocumentVersion[]> {
    const response = await this.httpService.get(
      `/studies/${studyId}/regulatory/approvals/${approvalId}/documents`,
    );
    return response.data as DocumentVersion[];
  }
  async downloadDocument(documentUrl: string): Promise<Blob> {
    const response = await this.httpService.get(documentUrl);
    return response.data as Blob;
  }
  async downloadDocumentStream(
    studyId: string,
    approvalId: string,
    documentId: string,
  ): Promise<Blob> {
    const response = await this.httpService.getBlob(
      `/studies/${studyId}/regulatory/approvals/${approvalId}/documents/${documentId}/download`,
    );
    return response;
  }
  async getTimeline(
    studyId: string,
    approvalId: string,
  ): Promise<TimelineEvent[]> {
    const response = await this.httpService.get(
      `/studies/${studyId}/regulatory/approvals/${approvalId}/timeline`,
    );
    return response.data as TimelineEvent[];
  }
  async getApprovalSummary(studyId: string): Promise<ApprovalSummary> {
    const response = await this.httpService.get(
      `/studies/${studyId}/regulatory/summary`,
    );
    return response.data as ApprovalSummary;
  }
  async getExpiryAlerts(
    studyId: string,
    withinDays?: number,
  ): Promise<ExpiryAlert[]> {
    let url = `/studies/${studyId}/regulatory/expiry-alerts`;
    if (withinDays) {
      url += `?days=${withinDays}`;
    }
    const response = await this.httpService.get<Record<string, unknown>[]>(url);
    const raw = response.data ?? [];
    const list = Array.isArray(raw) ? raw : [];
    return list.map(mapApprovalToExpiryAlert);
  }
  async getExpiryAlertsBySiteId(
    studyId: string,
    siteId: string,
    withinDays?: number,
  ): Promise<ExpiryAlert[]> {
    const params = new URLSearchParams();
    params.append("site_id", siteId);
    if (withinDays) {
      params.append("days", String(withinDays));
    }
    const url = `/studies/${studyId}/regulatory/expiry-alerts?${params.toString()}`;
    const response = await this.httpService.get<Record<string, unknown>[]>(url);
    const raw = response.data ?? [];
    const list = Array.isArray(raw) ? raw : [];
    return list.map(mapApprovalToExpiryAlert);
  }
  async checkActivationStatus(
    studyId: string,
    siteId: string,
  ): Promise<ActivationStatus> {
    const response = await this.httpService.get(
      `/studies/${studyId}/sites/${siteId}/regulatory/activation-status`,
    );
    return response.data as ActivationStatus;
  }
  async getApprovalsBySiteId(
    studyId: string,
    siteId: string,
    filters?: ApprovalFilters,
  ): Promise<RegulatoryApproval[]> {
    const params = new URLSearchParams();
    params.append("site_id", siteId);
    // Always add pagination parameters with defaults
    params.append("page", filters?.page ? String(filters.page) : "1");
    params.append("limit", filters?.limit ? String(filters.limit) : "20");
    // Add optional filter parameters
    if (filters) {
      if (filters.category) {
        params.append("category", String(filters.category).toUpperCase());
      }
      if (filters.status) {
        params.append("status", String(filters.status).toUpperCase());
      }
      if (filters.approvalType) {
        params.append(
          "approval_type",
          String(filters.approvalType).toUpperCase(),
        );
      }
      if (filters.showArchived !== undefined) {
        params.append("includeInactive", String(filters.showArchived));
      }
      if (filters.expiringWithinDays) {
        params.append(
          "expiring_within_days",
          String(filters.expiringWithinDays),
        );
      }
      if (filters.searchTerm) {
        params.append("search_term", filters.searchTerm);
      }
    }
    const url = `/studies/${studyId}/regulatory/approvals?${params.toString()}`;
    const response = await this.httpService.get(url);
    // Handle API response structure: { statusCode, message, data: { items: [...], total, page, limit } }
    if (
      response.data &&
      typeof response.data === "object" &&
      "data" in response.data &&
      response.data.data &&
      typeof response.data.data === "object" &&
      "items" in response.data.data &&
      Array.isArray(response.data.data.items)
    ) {
      return response.data.data.items as RegulatoryApproval[];
    }
    // Fallback if response.data is already the data object with items
    if (
      response.data &&
      typeof response.data === "object" &&
      "items" in response.data &&
      Array.isArray(response.data.items)
    ) {
      return response.data.items as RegulatoryApproval[];
    }
    // Fallback if response is already an array
    if (Array.isArray(response.data)) {
      return response.data as RegulatoryApproval[];
    }
    return [];
  }
  async getAuthorities(
    category?: ApprovalCategory,
    country?: string,
    active?: boolean,
  ): Promise<RegulatoryAuthority[]> {
    const params = new URLSearchParams();
    if (category) {
      params.append("category", category);
    }
    if (country) {
      params.append("country", country);
    }
    if (active !== undefined) {
      params.append("active", String(active));
    }
    const url = `/regulatory/authorities?${params.toString()}`;
    try {
      const response = await this.httpService.get(url);
      // Handle API response structure
      if (
        response.data &&
        typeof response.data === "object" &&
        "data" in response.data &&
        response.data.data &&
        typeof response.data.data === "object" &&
        "items" in response.data.data &&
        Array.isArray(response.data.data.items)
      ) {
        return response.data.data.items as RegulatoryAuthority[];
      }
      // Fallback if response.data is already the data object with items
      if (
        response.data &&
        typeof response.data === "object" &&
        "items" in response.data &&
        Array.isArray(response.data.items)
      ) {
        return response.data.items as RegulatoryAuthority[];
      }
      // Fallback if response is already an array
      if (Array.isArray(response.data)) {
        return response.data as RegulatoryAuthority[];
      }
      return [];
    } catch {
      return [];
    }
  }
}
